import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Button } from '../../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import {
  useGetWallGroup, useDeleteWallGroup, useStartGroup, useStopGroup, useRefreshGroup,
} from '../../../queries/wallGroups';
import { useGetVideoWalls, useStartWall, useStopWall, useGetWallMediaLibrary } from '../../../queries/videoWall';
import { useGetAssignments } from '../../../queries/wallAssignments';
import { useGetPlaylist } from '../../../queries/wallPlaylistLibrary';
import { useGetThemes } from '../../../queries/signageTemplates';
import ScheduleStatusBanner from './ScheduleStatusBanner';
import ChangePlaylistDialog from './ChangePlaylistDialog';
import GroupMembersDialog from './components/GroupMembersDialog';
import TemplateOrientationWarning from './components/TemplateOrientationWarning';
import PlaylistPreviewPlayer from './preview/PlaylistPreviewPlayer';
import useWallSyncTelemetry, { syncHealth, HEALTH_COLORS } from './useWallSyncTelemetry';

/**
 * Wall Group detail / command-center — brings a group up to parity with the
 * individual wall page (B + A of the Groups uplift). Composes existing pieces:
 * group fan-out controls, the schedule now/next banner, an offline preview of the
 * group's default playlist on the shared geometry, a live member-walls list (per-
 * wall sync dot via RTDB telemetry + click-through + inline Start/Stop), and the
 * generalized Change-Playlist dialog (group-scoped assignment). Read-only on the
 * sync path — every write is an existing playlist_assignments / fan-out call.
 */

/* One member wall row — owns its own live RTDB telemetry + inline controls. */
function MemberRow({ wall, navigate }) {
  const { t } = useTranslation();
  const tel = useWallSyncTelemetry(wall.id);
  const start = useStartWall(wall.id);
  const stop = useStopWall(wall.id);

  // Prefer live RTDB state; fall back to the static state from the group payload.
  const state = tel.state || (wall.state === 'playing' ? 'playing' : 'stopped');
  const playing = state === 'playing';
  const preparing = state === 'preparing';

  // Worst per-screen drift → the row dot (matches the wall page's health colours).
  const drifts = Object.values(tel.screens || {}).map((s) => s?.drift_ms).filter((d) => d != null);
  const worst = drifts.length ? Math.max(...drifts.map((d) => Math.abs(d))) : null;
  const health = playing ? syncHealth(worst) : 'unknown';
  const dot = preparing ? 'var(--pp-warning)' : playing ? HEALTH_COLORS[health] : 'var(--pp-offline, #94a3b8)';
  const stateLabel = preparing ? t('video_wall.state_preparing')
    : playing ? t('video_wall.state_playing') : t('video_wall.state_stopped');

  return (
    <div className="d-flex align-items-center gap-2 p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 8 }}>
      <span style={{ width: 12, height: 12, borderRadius: '50%', background: dot, flexShrink: 0 }} title={stateLabel} />
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{wall.name}</div>
        <small style={{ color: 'var(--pp-text-secondary)' }}>
          {wall.rows}×{wall.columns} · {stateLabel}
          {playing && tel.syncMode && ` · ${tel.syncMode === 'lan' ? t('video_wall.sync_mode_lan', { n: tel.masterPos ?? 1 }) : t('video_wall.sync_mode_cloud')}`}
        </small>
      </div>
      {playing ? (
        <Button icon="pi pi-stop-circle" rounded text severity="danger" size="small"
          aria-label={t('video_wall.groups.stop')} loading={stop.isLoading} onClick={() => stop.mutate()} />
      ) : (
        <Button icon="pi pi-play" rounded text severity="success" size="small"
          aria-label={t('video_wall.groups.start')} loading={start.isLoading} onClick={() => start.mutate({})} />
      )}
      <Button icon="pi pi-arrow-right" rounded text size="small"
        aria-label={t('video_wall.groups.open_wall')} onClick={() => navigate(`/apps/video-wall/${wall.id}`)} />
    </div>
  );
}

export default function VideoWallGroupShow() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: group, isLoading } = useGetWallGroup(id);
  const { data: walls = [] } = useGetVideoWalls();
  const del = useDeleteWallGroup();
  const start = useStartGroup();
  const stop = useStopGroup();
  const refresh = useRefreshGroup();

  const [membersOpen, setMembersOpen] = useState(false);
  const [changePlaylist, setChangePlaylist] = useState(false);
  const [contentPreview, setContentPreview] = useState(false);

  // The group's always-on DEFAULT assignment (what Change Playlist manages). Windowed
  // rows belong to the Schedule page; the now/next banner reflects whichever wins.
  const { data: assignments = [] } = useGetAssignments({ group_id: id });
  const assignment = assignments.find((a) => !a.start_time && !a.end_time && !a.days_of_week) || null;
  const hasWindowed = assignments.some((a) => a.start_time || a.end_time || a.days_of_week);
  const { data: assignedPlaylist } = useGetPlaylist(assignment?.playlist_id, !!assignment);
  const effItems = assignedPlaylist?.items || [];
  const hasContent = effItems.length > 0;

  // Offline preview plumbing (same as the wall page).
  const { data: themeData } = useGetThemes();
  const previewThemes = themeData?.themes ?? [];
  const previewZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const { data: previewMediaRaw } = useGetWallMediaLibrary(true);
  const previewMediaMap = useMemo(() => {
    const arr = previewMediaRaw?.data ?? previewMediaRaw ?? [];
    const m = {}; (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; }); return m;
  }, [previewMediaRaw]);
  const previewResolvePhoto = useMemo(() => (mid) => previewMediaMap[mid]?.path || previewMediaMap[mid]?.thumb || null, [previewMediaMap]);

  const members = group?.walls || [];
  const memberCount = members.length;
  const playingCount = members.filter((w) => w.state === 'playing').length;
  // Aggregate member state drives which primary action(s) to show — same pattern as
  // the single-wall Show.jsx (Start green / Stop red). allStopped includes the
  // no-members case so the controls never render empty.
  const allStopped = playingCount === 0;
  const allPlaying = memberCount > 0 && playingCount === memberCount;

  // Group fan-out actions also touch member states → refresh this group's detail.
  const runGroup = async (m) => { try { await m.mutateAsync(id); } finally { qc.invalidateQueries(['wallGroup', id]); } };

  const confirmDelete = () => confirmDialog({
    message: t('video_wall.groups.delete_confirm', { name: group?.name }),
    header: t('video_wall.groups.delete_title'),
    icon: 'pi pi-exclamation-triangle', acceptClassName: 'p-button-danger',
    acceptLabel: t('video_wall.groups.delete'), rejectLabel: t('video_wall.groups.cancel'),
    accept: async () => { await del.mutateAsync(id); navigate('/apps/video-wall/groups'); },
  });

  if (isLoading) return <Layout><Skeleton height="20rem" /></Layout>;
  if (!group) {
    return (
      <Layout>
        <div className="text-center p-5" style={{ color: 'var(--pp-text-secondary)' }}>
          <h4 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.groups.not_found')}</h4>
          <Button label={t('video_wall.groups.back')} icon="pi pi-arrow-left" outlined className="mt-2"
            onClick={() => navigate('/apps/video-wall/groups')} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmDialog />

      {/* Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <Button icon="pi pi-arrow-left" text rounded onClick={() => navigate('/apps/video-wall/groups')} />
          <div>
            <h3 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{group.name}</h3>
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              {group.rows ? `${group.rows}×${group.columns} · ${t(`video_wall.orientation_${group.orientation}`)}` : t('video_wall.groups.any_geometry')}
              {' · '}{t('video_wall.groups.walls_count', { n: memberCount })}
            </small>
          </div>
        </div>
        <span className="pp-status" style={{
          background: playingCount > 0 ? 'var(--pp-success)' : 'var(--pp-bg-tertiary)',
          color: playingCount > 0 ? '#fff' : 'var(--pp-text-secondary)',
          border: 'none', fontWeight: 600, padding: '4px 12px', borderRadius: 999,
        }}>
          {t('video_wall.groups.playing_count', { n: playingCount, total: memberCount })}
        </span>
      </div>

      <div className="row g-3">
        {/* Left — preview + members */}
        <div className="col-12 col-lg-8">
          {hasContent && (
            <div className="p-4 mb-3" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.preview_content')}</h6>
                <Button icon="pi pi-window-maximize" text rounded size="small"
                  aria-label={t('video_wall.preview_content')} onClick={() => setContentPreview(true)} />
              </div>
              <div className="d-flex justify-content-center">
                <PlaylistPreviewPlayer items={effItems} maxWidth={460} lockGrid
                  initialGrid={{ rows: group.rows || 1, cols: group.columns || 1, orientation: group.orientation || 'landscape' }}
                  mediaMap={previewMediaMap} themes={previewThemes} defaultZones={previewZones} resolvePhoto={previewResolvePhoto} />
              </div>
            </div>
          )}

          <div className="p-4" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.groups.members')}</h6>
              <Button label={t('video_wall.groups.manage_members')} icon="pi pi-users" size="small" outlined
                onClick={() => setMembersOpen(true)} />
            </div>
            {memberCount === 0 ? (
              <div className="text-center py-4">
                <p className="small mb-2" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.groups.no_members')}</p>
                <Button label={t('video_wall.groups.add_members')} icon="pi pi-plus" size="small" onClick={() => setMembersOpen(true)} />
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {members.map((w) => <MemberRow key={w.id} wall={w} navigate={navigate} />)}
              </div>
            )}
          </div>
        </div>

        {/* Right — schedule + controls + content */}
        <div className="col-12 col-lg-4">
          <ScheduleStatusBanner type="group" id={id} compact />

          {/* Template designed for the opposite orientation of this group's walls */}
          <TemplateOrientationWarning items={effItems} wallOrientation={group.orientation} />

          <div className="p-4 mb-3" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <h6 className="mb-2" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.controls')}</h6>
            <div className="d-grid gap-2">
              {/* Start (green) / Stop (red) — shown by aggregate member state: only
                  Start when all stopped, only Stop when all playing, both when mixed. */}
              {!allPlaying && (
                <Button label={t('video_wall.groups.start')} icon="pi pi-play" severity="success" size="large"
                  loading={start.isLoading} disabled={!memberCount} onClick={() => runGroup(start)} />
              )}
              {!allStopped && (
                <Button label={t('video_wall.groups.stop')} icon="pi pi-stop-circle" severity="danger" size="large"
                  loading={stop.isLoading} disabled={!memberCount} onClick={() => runGroup(stop)} />
              )}
              <Button label={t('video_wall.groups.refresh')} icon="pi pi-sync" outlined
                loading={refresh.isLoading} disabled={!memberCount} onClick={() => runGroup(refresh)} />
            </div>
            <div className="mt-3 pt-3 d-flex gap-2 flex-wrap" style={{ borderTop: '1px solid var(--pp-border)' }}>
              <Button label={t('video_wall.schedule.manage')} icon="pi pi-calendar" size="small" text
                onClick={() => navigate(`/apps/video-wall/schedule?group=${id}`)} />
              <Button label={t('video_wall.groups.delete')} icon="pi pi-trash" size="small" text severity="danger"
                className="ms-auto" onClick={confirmDelete} />
            </div>
          </div>

          <div className="p-4" style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}>
            <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
              <h6 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.groups.group_playlist')}</h6>
              <div className="d-flex gap-2 flex-wrap">
                <Button label={t('video_wall.groups.assign_playlist')} icon="pi pi-arrow-right-arrow-left" size="small"
                  disabled={!memberCount} onClick={() => setChangePlaylist(true)} />
                {assignment?.playlist_id && (
                  <Button label={t('video_wall.edit_in_library')} icon="pi pi-external-link" size="small" outlined
                    onClick={() => navigate(`/apps/video-wall/library/${assignment.playlist_id}`)} />
                )}
              </div>
            </div>
            {assignment ? (
              <div className="d-flex align-items-center gap-2 p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 8 }}>
                <i className="pi pi-list" style={{ color: 'var(--pp-primary)' }} />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
                    {assignedPlaylist?.name || assignment.playlist?.name || '—'}
                  </div>
                  <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.playlist_items_n', { n: effItems.length })}</small>
                </div>
              </div>
            ) : (
              <div>
                <p className="small mb-1" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.groups.no_group_playlist')}</p>
                <small style={{ color: 'var(--pp-text-secondary)' }}>
                  {hasWindowed ? t('video_wall.groups.scheduled_only') : t('video_wall.groups.no_group_playlist_hint')}
                </small>
              </div>
            )}
            {!memberCount && (
              <p className="small mt-2 mb-0" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.groups.assign_needs_members')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Offline preview (full size) */}
      <Dialog header={t('video_wall.preview_content')} visible={contentPreview} onHide={() => setContentPreview(false)}
        style={{ width: 'min(96vw, 780px)' }} dismissableMask>
        <div className="p-2 d-flex justify-content-center">
          <PlaylistPreviewPlayer items={effItems} maxWidth={700} lockGrid
            initialGrid={{ rows: group.rows || 1, cols: group.columns || 1, orientation: group.orientation || 'landscape' }}
            mediaMap={previewMediaMap} themes={previewThemes} defaultZones={previewZones} resolvePhoto={previewResolvePhoto} />
        </div>
      </Dialog>

      <GroupMembersDialog group={membersOpen ? group : null} walls={walls} onHide={() => setMembersOpen(false)} />

      <ChangePlaylistDialog
        visible={changePlaylist}
        onHide={() => setChangePlaylist(false)}
        groupId={id}
        currentPlaylistId={assignment?.playlist_id || null}
        assignmentId={assignment?.id || null}
      />
    </Layout>
  );
}
